<template>
  <app-page-container :custom="1600" :min-height="0">
    <app-page-header design="new" compact title="My tickets" />
    <div class="ui-page">
      <app-filter-toolbar>
        <template #create>
          <app-button design="new" @click.native="$modal.show('support-ticket-create')">Report a problem</app-button>
        </template>
        <template #primary>
          <app-input v-model="search" design="new" type="search" placeholder="Search my tickets" @input="scheduleLoad" />
        </template>
        <template #selection>
          <app-filters v-model="view" design="new" refined weight="medium" name="my-support-ticket-status" :filters="views" aria-label="Ticket status" />
        </template>
        <template #actions>
          <app-refresh-button :loading="loading" @click="load" />
        </template>
      </app-filter-toolbar>
      <app-content-skeleton v-if="loading" label="Loading tickets" :columns="columns" min-width="1200px" />
      <app-no-results
        v-else-if="!tickets.length"
        design="new"
        :title="search ? 'No matching tickets' : 'No tickets here yet'"
        :text="search ? 'Try changing the search query.' : 'Use Report a problem when you need help.'"
      />
      <app-card v-else design="new" variant="table" header-density="compact">
        <app-data-table design="new" density="dense" :rows="tickets" :columns="columns" min-width="1200px">
          <template #cell-ticket="{ row: ticket }">
            <button type="button" class="my-support-tickets__ticket" @click="openTicket(ticket)">{{ ticket.public_number }}</button>
          </template>
          <template #cell-description="{ row: ticket }">
            <span v-tooltip="tooltip([ticket.subject, ticket.description_preview].filter(Boolean).join(' — '))" class="my-support-tickets__description">
              <strong>{{ ticket.subject }}</strong><span v-if="ticket.description_preview"> — {{ ticket.description_preview }}</span>
            </span>
          </template>
          <template #cell-system="{ row: ticket }">
            <app-status-badge v-if="ticket.system" design="new" compact :label="ticket.system.name" :color="ticket.system.color" />
            <span v-else class="my-support-tickets__muted">—</span>
          </template>
          <template #cell-status="{ row: ticket }"><app-status-badge design="new" compact :label="statusLabel(ticket.status)" :tone="statusTone(ticket.status)" /></template>
          <template #cell-read="{ row: ticket }"><app-status-badge design="new" compact dot :label="ticket.is_unread ? 'Unread' : 'Read'" :tone="ticket.is_unread ? 'blue' : 'neutral'" /></template>
          <template #cell-assignee="{ row: ticket }"><app-table-user :name="ticket.assignee && ticket.assignee.full_name || ''" :photo="ticket.assignee && ticket.assignee.crop && ticket.assignee.crop.crop_url || ''" /></template>
          <template #cell-updated="{ row: ticket }"><time v-tooltip="tooltip(formatDateTime(ticket.last_activity_at))" :datetime="ticket.last_activity_at">{{ relativeTime(ticket.last_activity_at) }}</time></template>
        </app-data-table>
      </app-card>
    </div>
    <employee-ticket-modal v-if="$route.query.ticket" :ticket-id="Number($route.query.ticket)" @close="closeTicket" @updated="load" />
    <p v-if="error" class="my-support-tickets__error" role="alert">{{ error }}</p>
  </app-page-container>
</template>

<script>
import { getSupportTickets } from '@/http'
import EmployeeTicketModal from './components/EmployeeTicketModal'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppRefreshButton from '@/components/AppRefreshButton.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import { apiError, formatDateTime, normalizePage, relativeTime, statusLabel, statusTone } from './ticketState'

export default {
  name: 'MySupportTickets',
  components: { AppContentSkeleton, AppRefreshButton, AppTableUser, EmployeeTicketModal },
  data() {
    return {
      tickets: [],
      loading: true,
      error: '',
      search: '',
      view: 'open',
      timer: null,
      views: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'all', label: 'All' }],
      columns: [
        { key: 'ticket', label: 'Ticket', width: '135px' },
        { key: 'description', label: 'Description', width: '350px' },
        { key: 'system', label: 'System', width: '120px' },
        { key: 'status', label: 'Status', width: '125px' },
        { key: 'read', label: 'Messages', width: '105px' },
        { key: 'assignee', label: 'Assignee', width: '180px' },
        { key: 'updated', label: 'Updated', width: '105px' }
      ]
    }
  },
  watch: { view() { this.load() } },
  created() {
    this.load()
    this.$root.$on('support-ticket-created', this.load)
  },
  beforeDestroy() {
    window.clearTimeout(this.timer)
    this.$root.$off('support-ticket-created', this.load)
  },
  methods: {
    formatDateTime,
    relativeTime,
    statusLabel,
    statusTone,
    scheduleLoad() { window.clearTimeout(this.timer); this.timer = window.setTimeout(this.load, 250) },
    load() {
      this.loading = true
      const status = this.view === 'open' ? 'new,in_progress,waiting' : (this.view === 'closed' ? 'closed' : undefined)
      return getSupportTickets({ status, search: this.search || undefined, page_size: 100 })
        .then(response => { this.tickets = normalizePage(response.data) })
        .catch(error => { this.error = apiError(error) })
        .finally(() => { this.loading = false })
    },
    openTicket(ticket) { this.$router.push({ name: this.$route.name, query: { ticket: ticket.id } }).catch(() => {}) },
    closeTicket() { this.$router.push({ name: this.$route.name }).catch(() => {}) },
    tooltip(content) { return { content: content || '—', placement: 'top', offset: 8, classes: 'tooltip--new' } }
  }
}
</script>

<style lang="scss" scoped>
.my-support-tickets__ticket { padding: 0; border: 0; background: transparent; color: $ui-blue; cursor: pointer; font: inherit; font-weight: 600; }
.my-support-tickets__ticket:focus-visible { border-radius: 3px; outline: 2px solid $ui-blue; outline-offset: 3px; }
.my-support-tickets__description { display: block; overflow: hidden; color: $ui-text; text-overflow: ellipsis; white-space: nowrap; }
.my-support-tickets__description strong { color: $ui-ink; font-weight: 600; }
.my-support-tickets__muted { color: $ui-muted; }
.my-support-tickets__error { color: $ui-red; }
</style>
